import { Button, Group, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { GenericErrorPage } from '../../error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { EntityWithFile, UploadedFile } from '../hooks/file-uploads/types';
import { useFileUploads } from '../hooks/file-uploads/useFileUploads';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import FilesTable from './files-table/FilesTable';
import { IconFolderPlus, IconUpload } from '@tabler/icons-react';
import MoveFileModal from './files-table/MoveFileModal';
import FileUploadLayout from './FileUploadLayout';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { CreateFolderModal } from './modals/CreateFolderModal';
import { RenameFolderModal } from './modals/RenameFolderModal';

export default function FilesPage() {
  const { sectionSlug, folderId } = useParams<{ sectionSlug?: string; folderId?: string }>();

  const currentFolder =
    folderId || (sectionSlug?.startsWith('folder/') ? sectionSlug.replace('folder/', '') : null);
  const navigate = useNavigate();
  const { clientId, client } = useCurrentClient();
  const [searchValue, setSearchValue] = useState('');
  const [createFolderOpened, { open: openCreateFolder, close: closeCreateFolder }] =
    useDisclosure(false);
  const [selectedFileForMove, setSelectedFileForMove] = useState<UploadedFile | null>(null);
  const [moveModalOpened, { open: openMoveModal, close: closeMoveModal }] = useDisclosure(false);
  const [selectedFolderForRename, setSelectedFolderForRename] = useState<UploadedFile | null>(null);
  const [renameModalOpened, { open: openRenameModal, close: closeRenameModal }] =
    useDisclosure(false);
  const [fileToDelete, setFileToDelete] = useState<UploadedFile | null>(null);

  const {
    files,
    allOtherFiles,
    deleteFile,
    deleteError,
    resetDeleteError,
    isDeleting,
    createFolder,
    moveFile,
    isLoading: isLoadingFiles,
    error,
    refetchFiles,
  } = useFileUploads({
    autoRefreshProcessing: true,
  });
  const isLoading = isLoadingFiles;

  useEffect(() => {
    refetchFiles();
  }, [sectionSlug, folderId]);

  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();

  const handleDeletePress = (file: UploadedFile) => {
    resetDeleteError();
    setFileToDelete(file);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    try {
      await deleteFile(fileToDelete.id);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete file',
        'An error occurred while trying to delete the file. Please try again later.',
        e,
        { clientId, fileId: fileToDelete.id },
      );
    }
    setFileToDelete(null);
  };

  const handleViewFile = async (uploadedFile: UploadedFile) => {
    try {
      const link = await createLink(uploadedFile.file.id);
      window.open(link.url, '_blank');
    } catch (error) {
      notifyAndLogError(
        'Failed to open file',
        'An error occurred while trying to open the file. Please try again later.',
        error,
        { clientId, fileId: uploadedFile.file.id },
      );
    }
  };

  const handleFolderClick = (folder: EntityWithFile) => {
    navigate(`/client/${clientId}/document/files/folder/${folder.id}`);
  };

  const handleFileDocument = (file: UploadedFile) => {
    setSelectedFileForMove(file);
    openMoveModal();
  };

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        error,
        { clientId, fileId: file.id, parentId },
      );
    }
  };

  const getCurrentFolderName = () => {
    if (!currentFolder) {
      return `All ${client?.firstName || 'client'}'s Documents`;
    }

    const folder = files.find(f => f.id === currentFolder && f.isFolder);
    return folder?.file.fileName || 'Unknown Folder';
  };

  const handleCreateFolder = async (folderName: string) => {
    try {
      await createFolder(folderName, currentFolder ?? undefined);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to create folder',
        'An error occurred while trying to create the folder. Please try again later.',
        error,
        { clientId, folderName, currentFolder },
      );
      throw error;
    }
  };

  const handleRenameFolderClick = (folder: UploadedFile) => {
    setSelectedFolderForRename(folder);
    openRenameModal();
  };

  const handleUploadClick = () => {
    navigate(`/client/${clientId}/document/add`);
  };

  const filesByFolder = useMemo(
    () =>
      allOtherFiles.reduce((acc: { [parentId: string]: UploadedFile[] }, uploadedFile) => {
        const key = uploadedFile.parentId || 'root';
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(uploadedFile);
        return acc;
      }, {}),
    [allOtherFiles],
  );

  const filteredFiles = useMemo(() => {
    const currentFolderFiles = filesByFolder?.[currentFolder || 'root'] || [];

    if (!searchValue) {
      return currentFolderFiles;
    }

    return currentFolderFiles.filter(file =>
      file.file.fileName.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [filesByFolder, currentFolder, searchValue]);

  if (error || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  return (
    <FileUploadLayout
      title={getCurrentFolderName()}
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      actions={
        <Group gap="sm" wrap="nowrap">
          <Button
            variant="secondary"
            size="md"
            onClick={openCreateFolder}
            aria-label="Create folder"
            title="Create folder"
            px="xs"
          >
            <IconFolderPlus size={16} />
          </Button>
          <Button
            variant="primary"
            size="md"
            leftSection={<IconUpload size={16} />}
            onClick={handleUploadClick}
          >
            Upload
          </Button>
        </Group>
      }
    >
      <FilesTable<UploadedFile>
        entitiesWithFile={filteredFiles}
        isLoading={isLoading}
        onClick={(file: EntityWithFile) => (file.isFolder ? handleFolderClick(file) : null)}
        enableDragDrop={true}
        additionalColumns={[
          {
            label: 'Model',
            valueBuilder: f => {
              return <Text>{f.dataUploadModel?.displayName}</Text>;
            },
          },
        ]}
        actions={(file: UploadedFile) => {
          if (file.isFolder) {
            return [
              { label: 'Rename', action: handleRenameFolderClick },
              { label: 'Move', action: handleFileDocument },
              { label: 'Delete', action: handleDeletePress },
            ];
          } else {
            return [
              { label: 'View', action: handleViewFile },
              { label: 'Move', action: handleFileDocument },
              { label: 'Delete', action: handleDeletePress },
            ];
          }
        }}
      />

      <CreateFolderModal
        opened={createFolderOpened}
        onClose={closeCreateFolder}
        onCreateFolder={handleCreateFolder}
      />

      {selectedFileForMove && (
        <MoveFileModal
          opened={moveModalOpened}
          close={closeMoveModal}
          file={selectedFileForMove}
          onFileMoved={handleMoveFile}
        />
      )}

      {selectedFolderForRename && (
        <RenameFolderModal
          opened={renameModalOpened}
          onClose={closeRenameModal}
          folder={selectedFolderForRename}
          onRename={refetchFiles}
        />
      )}

      <ConfirmDeleteModal
        opened={!!fileToDelete}
        onClose={() => setFileToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={fileToDelete?.isFolder ? 'Delete folder' : 'Delete file'}
        message={`Are you sure you want to delete "${fileToDelete?.file.fileName}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeleting}
      />
    </FileUploadLayout>
  );
}
